import type { Lang } from "../types.ts";

export type InfoPageId = "help" | "tips" | "support" | "imprint";

export interface Section {
  heading?: string;
  /** Rendered as paragraphs; a nested array becomes a bullet list. */
  body: (string | string[])[];
}

export interface InfoPage {
  title: string;
  sections: Section[];
}

const de: Record<InfoPageId, InfoPage> = {
  help: {
    title: "Hilfe",
    sections: [
      {
        body: [
          "Diese App ist eine lokale Lernversion des amtlichen Fragenkatalogs für die theoretische Führerscheinprüfung. Alle Fragen, Bilder und Videos liegen auf deinem Rechner — es wird keine Internetverbindung benötigt.",
        ],
      },
      {
        heading: "Die Lernmodi",
        body: [
          [
            "Prüfung simulieren — 30 Fragen aus einem amtlichen Bogen, Auswertung erst am Ende, genau wie in der echten Prüfung.",
            "30 neue Fragen — Fragen, die du noch nie beantwortet hast.",
            "Alle Fragen & Themen — der komplette Katalog, frei durchblätterbar, mit Erklärung zu jeder Frage.",
            "Musterbögen — die amtlichen Prüfungsbögen einzeln auswählbar.",
            "Letzte Fehler — alles, was du zuletzt falsch beantwortet hast.",
            "Markierte Fragen — alles, was du mit dem Stern ☆ markiert hast.",
          ],
        ],
      },
      {
        heading: "Zwei Sprachen gleichzeitig",
        body: [
          "Oben rechts wählst du eine Hauptsprache und eine Zweitsprache. Jede Frage und jede Antwort wird dann in beiden Sprachen untereinander angezeigt. So lernst du den deutschen Prüfungswortlaut und verstehst gleichzeitig den Inhalt.",
          "Mit ⇄ (oder der Taste S) tauschst du die beiden Sprachen. Die Zweitsprache lässt sich auch ganz abschalten.",
        ],
      },
      {
        heading: "Bestehensregeln (Klasse B)",
        body: [
          "Jede Frage ist 2 bis 5 Fehlerpunkte wert. Bestanden hat, wer höchstens 10 Fehlerpunkte sammelt und nicht zwei Fragen mit je 5 Fehlerpunkten falsch beantwortet.",
        ],
      },
      {
        heading: "Tastatur",
        body: [
          "Die App lässt sich vollständig per Tastatur bedienen. Drücke ? für die Liste aller Tastenkürzel.",
        ],
      },
    ],
  },
  tips: {
    title: "Lerntipps",
    sections: [
      {
        body: ["Ein paar Dinge, die beim Lernen für die Theorieprüfung erfahrungsgemäß am meisten helfen."],
      },
      {
        heading: "Lieber täglich kurz als einmal lang",
        body: [
          "Zwanzig Minuten am Tag bringen mehr als vier Stunden am Wochenende. Der Katalog hat rund 1 100 Fragen für die Klasse B — verteilt auf zwei bis drei Wochen ist das gut zu schaffen.",
        ],
      },
      {
        heading: "Verstehen statt auswendig lernen",
        body: [
          "Viele Fragen sind Varianten desselben Prinzips. Lies nach jeder falschen Antwort die Erklärung — dann erkennst du die Regel wieder, auch wenn die Frage anders formuliert ist.",
        ],
      },
      {
        heading: "Fehler wiederholen",
        body: [
          "Nutze „Letzte Fehler“ regelmäßig. Was du einmal falsch hattest, kommt in der Prüfung mit hoher Wahrscheinlichkeit als Stolperstelle wieder.",
        ],
      },
      {
        heading: "Auf die Zahlenfragen achten",
        body: [
          "Abstände, Geschwindigkeiten, Promillegrenzen, Profiltiefe: Diese Werte muss man wirklich auswendig können. Sie tauchen fast in jeder Prüfung auf.",
        ],
      },
      {
        heading: "Videofragen ganz ansehen",
        body: [
          "Videofragen dürfen in der echten Prüfung nur zweimal abgespielt werden. Übe deshalb, beim ersten Ansehen gezielt auf andere Verkehrsteilnehmer und Verkehrszeichen zu achten.",
        ],
      },
      {
        heading: "Erst prüfungsreif, dann anmelden",
        body: [
          "Wenn du mehrere Prüfungssimulationen hintereinander mit 0 bis 4 Fehlerpunkten bestehst, bist du bereit.",
        ],
      },
    ],
  },
  support: {
    title: "Support",
    sections: [
      {
        body: [
          "Diese lokale App wird nicht kommerziell betrieben und hat keinen eigenen Support-Kanal.",
          "Fragen zum Inhalt des amtlichen Fragenkatalogs beantwortet deine Fahrschule. Für die Original-App und deren Support wende dich an theorie24.de.",
        ],
      },
      {
        heading: "Etwas funktioniert nicht?",
        body: [
          "Wenn Bilder oder Videos fehlen, wurden die Mediendateien noch nicht vollständig heruntergeladen. Führe im Projektverzeichnis bun run media erneut aus — der Download setzt dort fort, wo er abgebrochen ist.",
          "Dein Lernfortschritt liegt ausschließlich im lokalen Speicher deines Browsers. Wenn du den Browserverlauf löschst, ist er weg.",
        ],
      },
    ],
  },
  imprint: {
    title: "Impressum",
    sections: [
      {
        body: [
          "Dies ist eine private, lokal betriebene Lernanwendung ohne kommerziellen Zweck. Sie steht in keiner Verbindung zu den Rechteinhabern des Fragenkatalogs oder zum Anbieter der Original-App.",
        ],
      },
      {
        heading: "Herkunft der Inhalte",
        body: [
          "Fragen, Antworten, Erklärungen, Bilder und Videos stammen aus dem amtlichen Fragenkatalog zur Fahrerlaubnisprüfung und wurden der Web-App von theorie24.de entnommen. Alle Rechte an diesen Inhalten verbleiben bei ihren jeweiligen Rechteinhabern.",
          "Die deutschen und englischen Texte sind die amtlichen Fassungen. Die chinesische Übersetzung ist eine inoffizielle Lernhilfe und rechtlich nicht verbindlich — verbindlich ist in der Prüfung ausschließlich der deutsche Wortlaut.",
        ],
      },
      {
        heading: "Keine Gewähr",
        body: [
          "Für Vollständigkeit und Richtigkeit der hier angezeigten Inhalte wird keine Gewähr übernommen. Diese App ersetzt weder eine Fahrschule noch die amtliche Prüfungsvorbereitung.",
        ],
      },
    ],
  },
};

const en: Record<InfoPageId, InfoPage> = {
  help: {
    title: "Help",
    sections: [
      {
        body: [
          "This app is a local study copy of the official question catalogue for the German driving theory test. Every question, image and video is stored on your machine — no internet connection is needed.",
        ],
      },
      {
        heading: "The study modes",
        body: [
          [
            "Simulate the exam — 30 questions from an official sheet, marked only at the end, exactly like the real test.",
            "30 new questions — questions you have never answered before.",
            "All questions & topics — the complete catalogue, free to browse, with an explanation for every question.",
            "Sample sheets — the official exam sheets, selectable one by one.",
            "Last mistakes — everything you most recently got wrong.",
            "Marked questions — everything you starred with ☆.",
          ],
        ],
      },
      {
        heading: "Two languages at once",
        body: [
          "Pick a primary and a secondary language at the top right. Every question and answer is then shown in both, one under the other, so you learn the German exam wording while understanding what it means.",
          "Use ⇄ (or the S key) to swap the two languages. The secondary language can also be switched off entirely.",
        ],
      },
      {
        heading: "Passing rules (class B)",
        body: [
          "Each question is worth 2 to 5 error points. You pass with at most 10 error points, provided you did not get two 5-point questions wrong.",
        ],
      },
      {
        heading: "Keyboard",
        body: ["The app is fully keyboard operable. Press ? for the list of shortcuts."],
      },
    ],
  },
  tips: {
    title: "Learning tips",
    sections: [
      {
        body: ["A few things that tend to help most when studying for the theory test."],
      },
      {
        heading: "Little and often beats one long session",
        body: [
          "Twenty minutes a day gets you further than four hours at the weekend. The class B catalogue is around 1,100 questions — spread over two or three weeks that is very manageable.",
        ],
      },
      {
        heading: "Understand rather than memorise",
        body: [
          "Many questions are variations on the same principle. Read the explanation after every wrong answer, and you will recognise the rule again even when the question is worded differently.",
        ],
      },
      {
        heading: "Revisit your mistakes",
        body: [
          "Use “Last mistakes” regularly. Whatever caught you out once is very likely to catch you out again in the exam.",
        ],
      },
      {
        heading: "Watch the number questions",
        body: [
          "Distances, speed limits, blood alcohol limits, tread depth: these values genuinely have to be memorised, and they appear in almost every exam.",
        ],
      },
      {
        heading: "Watch video questions properly",
        body: [
          "In the real exam a video question may be played only twice. Practise noticing other road users and signs on the first viewing.",
        ],
      },
      {
        heading: "Book the test when you are ready",
        body: [
          "When you pass several exam simulations in a row with 0 to 4 error points, you are ready.",
        ],
      },
    ],
  },
  support: {
    title: "Support",
    sections: [
      {
        body: [
          "This local app is not run commercially and has no support channel of its own.",
          "Your driving school is the right place for questions about the content of the official catalogue. For the original app and its support, see theorie24.de.",
        ],
      },
      {
        heading: "Something not working?",
        body: [
          "If images or videos are missing, the media files were not fully downloaded. Run bun run media again in the project directory — the download resumes where it stopped.",
          "Your progress is stored only in your browser's local storage. Clearing your browser data deletes it.",
        ],
      },
    ],
  },
  imprint: {
    title: "Imprint",
    sections: [
      {
        body: [
          "This is a private, locally run study application with no commercial purpose. It is not affiliated with the rights holders of the question catalogue or with the publisher of the original app.",
        ],
      },
      {
        heading: "Where the content comes from",
        body: [
          "Questions, answers, explanations, images and videos come from the official German driving licence question catalogue and were taken from the theorie24.de web app. All rights in that content remain with their respective owners.",
          "The German and English texts are the official versions. The Chinese translation is an unofficial study aid and carries no legal weight — only the German wording is binding in the exam.",
        ],
      },
      {
        heading: "No warranty",
        body: [
          "No guarantee is given as to the completeness or accuracy of the content shown here. This app replaces neither a driving school nor official exam preparation.",
        ],
      },
    ],
  },
};

const zh: Record<InfoPageId, InfoPage> = {
  help: {
    title: "帮助",
    sections: [
      {
        body: [
          "本应用是德国驾照理论考试官方题库的本地学习版。所有题目、图片和视频都保存在你的电脑上，无需联网即可使用。",
        ],
      },
      {
        heading: "学习模式",
        body: [
          [
            "模拟考试 —— 从官方试卷中抽取 30 道题，答完后统一评分，与真实考试完全一致。",
            "30 道新题 —— 你从未做过的题目。",
            "全部题目与主题 —— 完整题库，可自由浏览，每题都有详细解析。",
            "样卷 —— 可逐套选择的官方试卷。",
            "最近错题 —— 你最近一次答错的所有题目。",
            "已标记题目 —— 你用 ☆ 标记过的题目。",
          ],
        ],
      },
      {
        heading: "双语对照",
        body: [
          "在右上角选择主语言和第二语言。每道题目和每个选项都会以两种语言上下并列显示，帮助你在理解题意的同时熟悉德语考试原文。",
          "点击 ⇄（或按 S 键）可交换两种语言，也可以完全关闭第二语言。",
        ],
      },
      {
        heading: "合格标准（B 类驾照）",
        body: [
          "每道题的错误分为 2 至 5 分。错误分不超过 10 分，且 5 分题答错不超过一道，即为通过。",
        ],
      },
      {
        heading: "键盘操作",
        body: ["本应用支持全键盘操作。按 ? 键可查看全部快捷键。"],
      },
    ],
  },
  tips: {
    title: "学习建议",
    sections: [
      { body: ["以下几点通常对备考理论考试帮助最大。"] },
      {
        heading: "每天少量练习胜过一次长时间突击",
        body: [
          "每天二十分钟比周末连做四小时更有效。B 类题库约有 1100 道题，分散到两三周内完成完全可行。",
        ],
      },
      {
        heading: "理解原理，而不是死记硬背",
        body: [
          "许多题目是同一原理的不同表述。每次答错后阅读解析，即使题目换个说法，你也能认出背后的规则。",
        ],
      },
      {
        heading: "反复复习错题",
        body: [
          "经常使用「最近错题」。做错过一次的题，在考试中很可能再次成为失分点。",
        ],
      },
      {
        heading: "特别注意数字题",
        body: [
          "安全距离、限速、酒精浓度上限、轮胎花纹深度等数值必须牢记，几乎每场考试都会出现。",
        ],
      },
      {
        heading: "认真观看视频题",
        body: [
          "真实考试中视频题最多只能播放两次。请练习在第一次观看时就留意其他交通参与者和交通标志。",
        ],
      },
      {
        heading: "准备好了再预约考试",
        body: ["如果你能连续多次以 0 至 4 个错误分通过模拟考试，就可以去考试了。"],
      },
    ],
  },
  support: {
    title: "支持",
    sections: [
      {
        body: [
          "本地应用非商业运营，没有独立的技术支持渠道。",
          "有关官方题库内容的疑问，请咨询你的驾校。原版应用及其支持服务请访问 theorie24.de。",
        ],
      },
      {
        heading: "遇到问题？",
        body: [
          "如果图片或视频缺失，说明媒体文件尚未下载完整。请在项目目录中重新运行 bun run media，下载会从中断处继续。",
          "你的学习记录仅保存在浏览器的本地存储中。清除浏览器数据会一并删除这些记录。",
        ],
      },
    ],
  },
  imprint: {
    title: "版本说明",
    sections: [
      {
        body: [
          "本应用为私人本地学习工具，不用于商业用途，与题库版权方及原版应用发行方均无关联。",
        ],
      },
      {
        heading: "内容来源",
        body: [
          "题目、答案、解析、图片和视频均来自德国驾照考试官方题库，取自 theorie24.de 的网页应用。相关内容的全部权利归各自权利人所有。",
          "德语和英语文本为官方版本。中文翻译为非官方学习辅助材料，不具法律效力；考试中仅以德语原文为准。",
        ],
      },
      {
        heading: "免责声明",
        body: [
          "本应用不对所显示内容的完整性和准确性作任何担保，也不能替代驾校培训或官方考试准备。",
        ],
      },
    ],
  },
};

export const PAGES: Record<Lang, Record<InfoPageId, InfoPage>> = { de, en, zh };
